import React, { useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import DirectorPanelState from './DirectorPanelState';

const TEMPLATE_HEADERS = [
  'record_type', 'import_action', 'location_id', 'employee_id', 'employee_name',
  'role', 'email', 'is_active', 'kpi_code', 'kpi_name', 'value',
  'period_type', 'period_value', 'notes'
].join(',');

const TEMPLATE_ROWS = [
  'MANAGER,UPSERT,NRH,MGR-001,Ana Torres,ops_manager,ana.torres@northstarops.com,TRUE,,,,,,New hire Apr 2026',
  'MANAGER,UPSERT,DTB,MGR-002,Ben Okafor,ops_manager,ben.okafor@northstarops.com,TRUE,,,,,,',
  'GOAL,UPSERT,NRH,,,,,,qualityCompletionRate,Quality Completion Rate,88,MONTH,2026-04,April quality completion target',
  'GOAL,UPSERT,NRH,,,,,,customerAdvocacyScore,Customer Advocacy Score,4.5,MONTH,2026-04,',
  'GOAL,UPSERT,NRH,,,,,,qualityCompletionRate,Quality Completion Rate,89,YEAR,2026,Annual goal',
  'ALLOCATION,UPSERT,NRH,MGR-001,,,,,,qualityCompletionRate,,MONTH,2026-04,Ana share of NRH qualityCompletionRate',
  'ALLOCATION,UPSERT,NRH,MGR-002,,,,,,qualityCompletionRate,,MONTH,2026-04,Ben share of NRH qualityCompletionRate',
  'ACTUAL,UPSERT,,MGR-001,,,,,,qualityCompletionRate,86.2,MONTH,2026-03,March final',
  'ACTUAL,UPSERT,,MGR-002,,,,,,qualityCompletionRate,89.1,MONTH,2026-03,March final',
  'ACTUAL,SKIP,,MGR-001,,,,,,customerAdvocacyScore,,MONTH,2026-03,Already imported'
];

function downloadTemplate() {
  const csv = [TEMPLATE_HEADERS, ...TEMPLATE_ROWS].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'unified-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

const TYPE_LABELS = {
  MANAGER: 'Managers',
  GOAL: 'Goals',
  ALLOCATION: 'Allocations',
  ACTUAL: 'Actuals'
};

function TypeBreakdown({ byType }) {
  if (!byType) return null;

  return (
    <div className="unified-import-type-breakdown">
      {Object.entries(TYPE_LABELS).map(([type, label]) => {
        const counts = byType[type] || {};
        const total = (counts.create || 0) + (counts.update || 0) + (counts.unchanged || 0) + (counts.error || 0);
        if (total === 0) return null;
        return (
          <div key={type} className="unified-import-type-row">
            <span className="unified-import-type-label">{label}</span>
            <span className="unified-import-type-counts">
              {counts.create > 0 && <em className="unified-import-chip create">{counts.create} new</em>}
              {counts.update > 0 && <em className="unified-import-chip update">{counts.update} update</em>}
              {counts.unchanged > 0 && <em className="unified-import-chip unchanged">{counts.unchanged} unchanged</em>}
              {counts.error > 0 && <em className="unified-import-chip error">{counts.error} error</em>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function UnifiedCsvImportCard({ onApplied }) {
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const topErrors = useMemo(() => (preview?.errors || []).slice(0, 6), [preview]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setFileName(file.name);
    setCsvText(text);
    setPreview(null);
    setMessage('');
    setError('');
  };

  const handlePreview = async () => {
    if (!csvText) {
      setError('Choose a CSV file before previewing.');
      return;
    }
    setIsPreviewLoading(true);
    setError('');
    setMessage('');
    try {
      const payload = await apiRequest('/api/performance-import/unified-preview', {
        method: 'POST',
        body: JSON.stringify({ csvText, fileName })
      });
      setPreview(payload);
      setMessage(`Preview ready — ${payload.summary.validRows} valid, ${payload.summary.invalidRows} invalid across ${payload.summary.totalRows} rows.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to preview CSV.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!preview?.previewToken || !csvText) {
      setError('Preview the file before applying.');
      return;
    }
    setIsApplyLoading(true);
    setError('');
    setMessage('');
    try {
      const payload = await apiRequest('/api/performance-import/unified-apply', {
        method: 'POST',
        body: JSON.stringify({ csvText, fileName, previewToken: preview.previewToken })
      });
      setPreview(payload);
      const { applied } = payload;
      const parts = [];
      if (applied.managerCount > 0) parts.push(`${applied.managerCount} manager(s)`);
      if (applied.goalCount > 0) parts.push(`${applied.goalCount} goal(s)`);
      if (applied.allocationCount > 0) parts.push(`${applied.allocationCount} allocation(s)`);
      if (applied.actualCount > 0) parts.push(`${applied.actualCount} actual(s)`);
      setMessage(`Applied: ${parts.join(', ') || 'nothing to apply'}.`);
      if (onApplied) onApplied(payload);
    } catch (requestError) {
      setError(requestError.message || 'Unable to apply import.');
    } finally {
      setIsApplyLoading(false);
    }
  };

  return (
    <section className="director-card unified-import-card">
      <div className="role-section-header">
        <h2>Unified Import</h2>
        <span>all record types</span>
      </div>
      <p className="role-section-copy">
        Import managers, goals, allocations, and actuals from a single CSV file. Use the <code>record_type</code> column to mix record types in one upload.
      </p>
      <p className="director-muted">
        Required columns: <code>record_type</code>, <code>location_id</code>, <code>kpi_code</code>, <code>value</code>, <code>period_type</code>, <code>period_value</code>.
        Period formats: <code>2026-04</code> (month), <code>2026-W14</code> (week), <code>2026-Q2</code> (quarter), <code>2026</code> (year).
      </p>
      <div className="director-form-actions director-form-actions-spread">
        <Button size="sm" variant="secondary" onClick={downloadTemplate}>
          Download Template
        </Button>
      </div>
      <label className="director-upload-card">
        <span>{fileName || 'Choose Unified CSV File'}</span>
        <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
      </label>
      <div className="director-form-actions director-form-actions-spread">
        <Button size="sm" variant="secondary" onClick={handlePreview} disabled={!csvText} loading={isPreviewLoading} loadingLabel="Previewing...">
          Preview Import
        </Button>
        <Button onClick={handleApply} disabled={!preview?.previewToken || preview?.summary?.validRows === 0} loading={isApplyLoading} loadingLabel="Applying...">
          Apply Valid Rows
        </Button>
      </div>

      <DirectorPanelState kind="success" message={!error ? message : ''} />
      <DirectorPanelState kind="error" message={error} />

      {preview && (
        <div className="director-import-preview">
          <div className="director-import-summary">
            <div className="director-import-stat">
              <span>Total</span>
              <strong>{preview.summary.totalRows}</strong>
            </div>
            <div className="director-import-stat">
              <span>Create</span>
              <strong>{preview.summary.createCount}</strong>
            </div>
            <div className="director-import-stat">
              <span>Update</span>
              <strong>{preview.summary.updateCount}</strong>
            </div>
            <div className="director-import-stat">
              <span>Unchanged</span>
              <strong>{preview.summary.unchangedCount || 0}</strong>
            </div>
            <div className="director-import-stat">
              <span>Errors</span>
              <strong>{preview.summary.invalidRows}</strong>
            </div>
          </div>
          <TypeBreakdown byType={preview.summary.byType} />
          {topErrors.length > 0 && (
            <div className="director-import-errors">
              <strong>Top Errors</strong>
              <ul className="director-bullet-list">
                {topErrors.map(item => (
                  <li key={`${item.rowNumber}-${item.field}-${item.message}`}>
                    Row {item.rowNumber}: [{item.field}] {item.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default UnifiedCsvImportCard;
